import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ROUTE_RELOAD_HOP_URL } from '../../components/common/route-reload-hop/route-reload-hop.component';

/**
 * Remembers the URL this tab was on immediately before the one it is on now,
 * across the whole app (not scoped to any one feature).
 *
 * ## Why this exists
 *
 * `FilingFormContainerComponent`'s `PERM` redirect needs to return the user
 * to wherever they came from. `Location.back()` was tried first and
 * abandoned: going BACK through browser history can restore the destination
 * page from the **back-forward cache (bfcache)** rather than performing any
 * navigation at all — confirmed directly (2026-10-05, DevTools console:
 * `Page entered Back-Forward Cache` on every open SignalR hub) on a tab with
 * a long history. A bfcache restore freezes and later unfreezes the ENTIRE
 * page, Angular included, with **no script running during the transition** —
 * no `popstate`, no Angular navigation, nothing this app can hook. Going
 * FORWARD — i.e. `router.navigate()`/`router.navigateByUrl()` to the exact
 * URL the user came from, as a brand-new history entry — never has this
 * problem: a freshly pushed entry has never been frozen, so there is nothing
 * for the browser to restore instead of actually navigating. This service is
 * what supplies that destination URL.
 *
 * ## Must be instantiated EAGERLY
 *
 * Exactly like {@link CurrentSheetCacheService}, which carries the identical
 * eager-injection comment at its own call site: if this service is only
 * constructed the first time something reads from it (e.g. when the
 * forbidden conversion page itself mounts), it has not been listening to any
 * navigation before that point and has nothing to report. `MainLayoutComponent`
 * injects this eagerly for exactly that reason — it must already be tracking
 * before the user ever navigates to a conversion that turns out to be
 * forbidden.
 */
@Injectable({ providedIn: 'root' })
export class PreviousUrlTrackerService {
  private readonly router = inject(Router);

  private previousUrl: string | null = null;
  private currentUrl: string | null = null;
  /** Set by the `ROUTE_RELOAD_HOP_URL` leg of a Retry; consumed by the very next NavigationEnd. */
  private returningFromReloadHop = false;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        // `FilingFormContainerComponent.retryLoad` re-creates the page by going
        // page → ROUTE_RELOAD_HOP_URL → the same page. Neither leg is a real
        // move, so neither may shift the URLs: recording the hop would make a
        // retried load that comes back PERM "return" the user to the blank hop,
        // and recording the return leg would make it "return" them to the very
        // page that just failed. So skip the hop, and skip the leg that follows
        // it when it lands back on the URL we were already on.
        const arrivedUrl = event.urlAfterRedirects;
        if (arrivedUrl === ROUTE_RELOAD_HOP_URL) {
          this.returningFromReloadHop = true;
          return;
        }
        const isReloadHopReturn = this.returningFromReloadHop && arrivedUrl === this.currentUrl;
        this.returningFromReloadHop = false;
        if (isReloadHopReturn) {
          return;
        }
        this.previousUrl = this.currentUrl;
        this.currentUrl = arrivedUrl;
      });
  }

  /**
   * The URL this tab was on immediately before the current one.
   *
   * `null` means there is no such URL — this is the first navigation this
   * tab has made (a conversion link pasted fresh, or the only page this
   * tab has ever shown), which is also what `window.history.length <= 2`
   * detects independently at the one call site that needs both signals.
   */
  getPreviousUrl(): string | null {
    return this.previousUrl;
  }
}
